'use client';

import { type PrimitiveProps, useFrame } from '@react-three/fiber';
import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { type Group, Box3, Object3D, Quaternion, Vector3 } from 'three';

import useTilesStore from 'state/tilesStore';

import { type Place, locationToEcef, locationToSurfaceRotation } from './geo';
import type { TileInfo } from './loader';
import Tile from './Tile';

export interface TilesProps extends Omit<PrimitiveProps, 'object'> {
  rotation?: number[];
  position?: Vector3;
}

export interface TilesRef {
  container: Object3D;
  initialize: (place: Place, tilesInfo: TileInfo[]) => void;
  reset: () => void;
}

const Tiles = forwardRef<TilesRef, TilesProps>(({ ...props }, ref) => {
  const [hemisphereRadius, setHemisphereRadius] = useState(0);

  const setAttribution = useTilesStore(s => s.setAttribution);

  const pivotRef = useRef(new Object3D()); // default Tiles axes are rotated counterintuitive
  const tilesRef = useRef<Group>(null!);

  const offsetRef = useRef<Vector3>(null!);
  const rotationQuaternionRef = useRef<Quaternion>(new Quaternion());
  const isInitializedRef = useRef(false);

  const setPlace = (place: Place) => {
    const targetQuat = locationToSurfaceRotation(place);

    rotationQuaternionRef.current = targetQuat;

    const center = locationToEcef(place);
    offsetRef.current = new Vector3(center.x, center.z, -center.y).negate();
  };

  const setTiles = (tilesInfo: TileInfo[]) => {
    const tiles = tilesInfo.map(tileInfo => {
      return new Tile({ ...tileInfo, offset: offsetRef.current });
    });

    const copyrightMap = new Map<string, number>();

    for (const tile of tiles) {
      if (!tile.copyright) continue;
      for (const part of tile.copyright.split(';')) {
        const count = copyrightMap.get(part) ?? 0;
        copyrightMap.set(part, count + 1);
      }
    }

    const copyrightArray = Array.from(copyrightMap);
    copyrightArray.sort((a, b) => b[1] - a[1]);

    const attribution = copyrightArray.map(c => c[0]);
    setAttribution(attribution);

    tilesRef.current.add(...tiles);
  };

  const initialize = (place: Place, tilesInfo: TileInfo[]) => {
    if (isInitializedRef.current) return;

    setPlace(place);
    setTiles(tilesInfo);
    isInitializedRef.current = true;
  };

  useImperativeHandle(ref, () => ({
    container: pivotRef.current,
    initialize,
    reset: () => {
      isInitializedRef.current = false;
      tilesRef.current.children.forEach(child => child.removeFromParent());
    },
  }));

  useFrame(() => {
    if (!isInitializedRef.current || !tilesRef.current) return;

    const aabb = new Box3();
    aabb.setFromObject(tilesRef.current);
    setHemisphereRadius(Math.abs(aabb?.min?.x / 1.5) ?? 0);

    tilesRef.current.setRotationFromQuaternion(rotationQuaternionRef.current);
    pivotRef.current.add(tilesRef.current);
  });

  return (
    <>
      <primitive object={pivotRef.current} {...props}>
        <group name="tilesContainer" ref={tilesRef} />

        <mesh position={[0, -100, 0]}>
          <sphereGeometry
            args={[
              hemisphereRadius ? 500 : 0,
              32,
              32,
              0,
              Math.PI * 2,
              Math.PI / 2,
              Math.PI / 2,
            ]}
          />
          <meshBasicMaterial color="black" side={1} />
        </mesh>
      </primitive>
    </>
  );
});

Tiles.displayName = 'Tiles';

export default Tiles;
